class Cart {
  constructor (drawer, updateKey) {
    this.drawer = drawer
    this.updateKey = updateKey
  }

  async updateCartSections () {
    if (!this.drawer.activator.cartItem) return

    const sections = this.drawer.activator.cartItem.sections
    const fetchedSections = await window.BAO.sectionRenderer.fetch(
      sections.map(s => s.section),
    )

    Object.keys(fetchedSections).forEach(key => {
      sections.find(s => s.section === key).html = fetchedSections[key]
    })

    window.BAO.sectionRenderer.renderMultiple(sections)
  }

  get giftMessage () {
    return this.drawer.giftMessage
  }

  get loading () {
    return this.drawer.els.apply.element.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.drawer.els.apply.element.setAttribute('aria-busy', val.toString())
  }
}

class CartAdder extends Cart {
  async add (key) {
    this.loading = true

    const uuid = Date.now().toString()

    const state = await window.BAO.Cart.cartGet()
    const coreLineItem = state.items.find(item => item.key === key)

    await window.BAO.Cart.updateItemProperties(
      coreLineItem.key,
      coreLineItem.quantity,
      {
        ...(coreLineItem?.properties ?? []),
        'Gift Message': this.giftMessage,
        [window.BAO.Gifting.PROPERTY_KEYS.ID]: uuid.toString(),
        [window.BAO.Gifting.PROPERTY_KEYS.CORE_PRODUCT]: 'true',
        [window.BAO.Gifting.PROPERTY_KEYS.GIFTING_MESSAGE]: this.drawer
          .activator.giftingMessage,
      },
    )

    const itemsToAdd = []

    const packagingId = this.drawer.activator.packagingId

    if (packagingId) {
      itemsToAdd.push({
        id: packagingId,
        quantity: 1,
        properties: {
          [window.BAO.Gifting.PROPERTY_KEYS.ID]: uuid,
          [window.BAO.Gifting.PROPERTY_KEYS.PACKAGING_PRODUCT]: 'true',
          [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
        },
      })
    }

    const extrasIds = this.drawer.activator.extrasIds
    if (extrasIds.length > 0) {
      extrasIds.forEach((id, i) => {
        const freeItem = this.drawer.activator.extrasIdsFree[i]
        itemsToAdd.push({
          id,
          quantity: 1,
          properties: {
            [window.BAO.Gifting.PROPERTY_KEYS.ID]: uuid,
            [window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.FREE_AFTER_1]: freeItem,
          },
        })
      })
    }

    await window.BAO.Cart.addItem(itemsToAdd)

    await this.updateCartSections()

    this.loading = false
  }
}

class CartUpdater extends Cart {
  async update () {
    this.drawer.els.apply.element.setAttribute('aria-busy', 'true')

    const state = await window.BAO.Cart.cartGet()
    const coreLineItem = this.findCoreLineItem(state.items)
    const currentItems = this.findCurrentItems(state.items)
    const newItems = this.findNewItems()

    this.drawer.log('Items', { coreLineItem, currentItems, newItems })

    const removedItems = this.findRemovedItems(currentItems, newItems)
    const addedItems = this.findAddedItems(currentItems, newItems)

    this.drawer.log('Changes', { addedItems, removedItems })

    await this.updateProperties(coreLineItem)

    if (removedItems.length > 0) {
      await window.BAO.Cart.removeItems(removedItems)
    }

    if (addedItems.length > 0) {
      await window.BAO.Cart.addItem(addedItems)
    }

    await this.updateCartSections()

    this.drawer.els.apply.element.setAttribute('aria-busy', 'false')
  }

  async remove ({ busyElement = this.drawer.els.reset.element } = {}) {
    busyElement.setAttribute('aria-busy', 'true')

    const state = await window.BAO.Cart.cartGet()
    const coreLineItem = this.findCoreLineItem(state.items)
    const currentItems = this.findCurrentItems(state.items)

    this.drawer.log('remove', { state, coreLineItem, currentItems })

    await this.removeProperties(coreLineItem)

    const itemsToRemove = []
    if (currentItems.packaging) {
      itemsToRemove.push(currentItems.packaging.key)
    }

    if (currentItems.extras.length > 0) {
      currentItems.extras.forEach(item => itemsToRemove.push(item.key))
    }

    this.drawer.log('Items to remove', { itemsToRemove })

    if (itemsToRemove.length > 0) {
      await window.BAO.Cart.removeItems(itemsToRemove)
    }

    await this.updateCartSections()

    busyElement.setAttribute('aria-busy', 'false')
  }

  /**
   * @param {{ packaging: ShopifyLineItem, extras: ShopifyLineItem[] }} currentItems
   * @param {{ packaging?: HTMLDivElement, extras: HTMLDivElement[] }} newItems
   *
   * @returns {PotentialCartItem[]}
   */
  findAddedItems (currentItems, newItems) {
    const addedItems = []

    const packagingIsDifferent =
      Number(newItems.packaging?.dataset.variantId) !==
      currentItems?.packaging?.variant_id

    if (packagingIsDifferent && newItems.packaging) {
      addedItems.push({
        id: newItems.packaging.dataset.variantId,
        quantity: 1,
        properties: {
          [window.BAO.Gifting.PROPERTY_KEYS.ID]: this.updateKey,
          [window.BAO.Gifting.PROPERTY_KEYS.PACKAGING_PRODUCT]: 'true',
          [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
        },
      })
    }

    newItems.extras.forEach(el => {
      const exists = currentItems.extras.find(
        extra => Number(el.dataset.variantId) === extra.variant_id,
      )

      if (!exists) {
        addedItems.push({
          id: el.dataset.variantId,
          quantity: 1,
          properties: {
            [window.BAO.Gifting.PROPERTY_KEYS.ID]: this.updateKey,
            [window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.FREE_AFTER_1]:
              el.dataset.free === 'true',
          },
        })
      }
    })

    return addedItems
  }

  /**
   * @param {{ packaging: ShopifyLineItem, extras: ShopifyLineItem[] }} currentItems
   * @param {{ packaging?: HTMLDivElement, extras: HTMLDivElement[] }} newItems
   *
   * @returns {string[]}
   */
  findRemovedItems (currentItems, newItems) {
    const removedItems = []

    const packagingIsDifferent =
      Number(newItems.packaging?.dataset.variantId) !==
      currentItems?.packaging?.variant_id

    if (packagingIsDifferent && currentItems.packaging) {
      removedItems.push(currentItems?.packaging?.key)
    }

    currentItems.extras.forEach(extra => {
      const stillExists = newItems.extras.find(
        el => Number(el.dataset.variantId) === extra.variant_id,
      )

      if (!stillExists) {
        removedItems.push(extra.key)
      }
    })

    return removedItems
  }

  findCoreLineItem (items) {
    return items.find(
      item =>
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.ID] ===
          this.updateKey &&
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.CORE_PRODUCT] ===
          'true',
    )
  }

  findCurrentItems (items) {
    return {
      packaging: this.#findCurrentPackagingItem(items),
      extras: this.#findCurrentExtrasItems(items),
    }
  }

  findNewItems () {
    return {
      packaging: this.#findNewPackagingItem(),
      extras: this.#findNewExtrasItems(),
    }
  }

  async updateProperties (lineItem) {
    await window.BAO.Cart.updateItemProperties(
      lineItem.key,
      lineItem.quantity,
      {
        ...lineItem.properties,
        [window.BAO.Gifting.PROPERTY_KEYS.GIFT_MESSAGE]: this.giftMessage,
        [window.BAO.Gifting.PROPERTY_KEYS.GIFTING_MESSAGE]: this.drawer
          .activator.giftingMessage,
      },
    )
  }

  async removeProperties (lineItem) {
    const newProperties = { ...lineItem.properties }

    delete newProperties[window.BAO.Gifting.PROPERTY_KEYS.ID]
    delete newProperties[window.BAO.Gifting.PROPERTY_KEYS.CORE_PRODUCT]
    delete newProperties[window.BAO.Gifting.PROPERTY_KEYS.GIFTING_MESSAGE]
    delete newProperties[window.BAO.Gifting.PROPERTY_KEYS.GIFT_MESSAGE]

    await window.BAO.Cart.updateItemProperties(
      lineItem.key,
      lineItem.quantity,
      newProperties,
    )
  }

  #findCurrentPackagingItem (items) {
    return items.find(
      item =>
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.ID] ===
          this.updateKey &&
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.PACKAGING_PRODUCT] ===
          'true',
    )
  }

  #findCurrentExtrasItems (items) {
    return items.filter(
      item =>
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.ID] ===
          this.updateKey &&
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT] ===
          'true',
    )
  }

  #findNewPackagingItem () {
    return this.drawer.currentPackagingChoice
  }

  #findNewExtrasItems () {
    return this.drawer.activeExtras
  }
}

class GiftDrawer extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['reset', 'apply']
  }

  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.activator = null

    this.initialPackagingChoice = this.packaging.find(
      el => el.getAttribute('aria-selected') === 'true',
    )
    this.currentPackagingChoice = this.initialPackagingChoice
  }

  setupListeners () {
    super.setupListeners()

    this.onPackagingClick = this.onPackagingClick.bind(this)
    this.onExtraClick = this.onExtraClick.bind(this)
    this.onResetClick = this.onResetClick.bind(this)
    this.onApplyClick = this.onApplyClick.bind(this)

    this.listeners.add(this, 'click', this.onPackagingClick)
    this.listeners.add(this, 'click', this.onExtraClick)
    this.listeners.add(this.els.reset.element, 'click', this.onResetClick)
    this.listeners.add(this.els.apply.element, 'click', this.onApplyClick)

    if (this.els?.message?.element) {
      this.onMessageChange = this.onMessageChange.bind(this)

      this.listeners.add(
        this.els.message.element,
        'input',
        this.onMessageChange,
      )
    }
  }

  open () {
    window.drawers.activeDrawerKey = this.drawerKey
  }

  close () {
    window.drawers.activeDrawerKey = ''
  }

  onPackagingClick (event) {
    if (!this.els.packaging?.selector) {
      return
    }

    const target = event.target.closest(this.els.packaging.selector)
    if (!target) return

    this.currentPackagingChoice = target
  }

  onExtraClick (event) {
    if (!this.els.extra?.selector) {
      return
    }

    const target = event.target.closest(this.els.extra.selector)
    if (!target) return
    if (target.getAttribute('aria-disabled') === 'true') {
      const packagingThatCanHaveExtras =
        this.packaging.length === 0
          ? true
          : this.packaging.find(el => el.dataset.canHaveExtras === 'true')
      if (!packagingThatCanHaveExtras) return

      this.currentPackagingChoice = packagingThatCanHaveExtras
    }

    target.setAttribute(
      'aria-selected',
      (target.getAttribute('aria-selected') !== 'true').toString(),
    )
  }

  async reset ({ busyElement = this.els.reset.element } = {}) {
    this.currentPackagingChoice = this.initialPackagingChoice

    this.activator.packagingId = ''
    this.activator.extrasIds = []
    this.activator.extrasIdsFree = []
    this.activator.giftMessage = ''
    this.activator.els.added.element.textContent = ''
    this.activator.hasGifting = false

    this.giftMessage = ''

    this.extras.forEach(el => el.setAttribute('aria-selected', 'false'))

    if (this.activator.applyAsUpdate) {
      const cartUpdater = new CartUpdater(this, this.activator.updateKey)

      await cartUpdater.remove({ busyElement })
    }

    this.activator = null

    this.close()
  }

  async apply () {
    this.activator.hasGifting = true

    this.log('onApplyClick', { activator: this.activator })

    this.activator.els.added.element.textContent = this.getGiftingMessage()
    this.activator.els.added.element.value = this.getGiftingMessageValue()
    this.activator.packagingId = this.currentPackagingChoice
      ? Number(this.currentPackagingChoice.dataset.variantId)
      : null
    this.activator.extrasIds = this.activeExtras.map(el =>
      Number(el.dataset.variantId),
    )
    this.activator.extrasIdsFree = this.activeExtras.map(el => el.dataset.free)
    this.activator.giftMessage = this.giftMessage
    if (this.activator.applyAsAdd) {
      const cartAdder = new CartAdder(this, this.activator.updateKey)

      await cartAdder.add(this.activator.updateKey)
    } else if (this.activator.applyAsUpdate) {
      const cartUpdater = new CartUpdater(this, this.activator.updateKey)

      await cartUpdater.update()
    }

    this.activator = null

    this.close()
  }

  onApplyClick () {
    if (
      this.giftMessage.length === 0 &&
      !this.currentPackagingChoice &&
      this.activeExtras.length === 0
    ) {
      this.reset({ busyElement: this.els.apply.element })
      return
    }

    this.apply()
  }

  onResetClick () {
    this.reset()
  }

  onMessageChange (event) {
    this.els.characterCount.element.textContent = `${event.target.value.length}/${this.els.characterCount.element.dataset.maxCharacters}`
  }

  getGiftingMessageValue () {
    const items = [this.currentPackagingChoice, ...this.activeExtras].filter(
      Boolean,
    )

    const parts = []

    if (items.length > 0) {
      const itemsString = items
        .map(item =>
          item.querySelector(this.els.title.selector)?.dataset.value?.trim(),
        )
        .filter(Boolean)
        .join(', ')

      parts.push(itemsString)
    }

    if (this.giftMessage) {
      parts.push('Free Gift Message Added')
    }

    return parts.join(' / ')
  }

  getGiftingMessage () {
    const items = [this.currentPackagingChoice, ...this.activeExtras].filter(
      Boolean,
    )

    const parts = []

    if (items.length > 0) {
      const itemsString = items
        .map(item =>
          item.querySelector(this.els.title.selector)?.textContent?.trim(),
        )
        .filter(Boolean)
        .join(', ')

      parts.push(itemsString)
    }

    if (this.giftMessage) {
      parts.push('Free Gift Message Added')
    }

    return parts.join(' / ')
  }

  get drawerKey () {
    return this.closest('site-drawer').getAttribute('key')
  }

  get packaging () {
    return this.els.packaging?.elements ?? []
  }

  get extras () {
    return this.els.extra?.elements ?? []
  }

  get activeExtras () {
    return this.extras.filter(
      extra => extra.getAttribute('aria-selected') === 'true',
    )
  }

  get currentPackagingChoice () {
    return this._currentPackagingChoice
  }

  set currentPackagingChoice (val) {
    this._currentPackagingChoice = val

    const canHaveExtras =
      val === undefined ? true : val?.dataset.canHaveExtras === 'true'

    this.packaging.forEach(el =>
      el.setAttribute('aria-selected', (el === val).toString()),
    )
    this.extras.forEach(extra => {
      extra.setAttribute('aria-disabled', (!canHaveExtras).toString())

      if (canHaveExtras === false) {
        extra.setAttribute('aria-selected', 'false')
      }
    })
  }

  get giftMessage () {
    return this.els?.message?.element?.value
  }

  set giftMessage (val) {
    if (!this.els?.message) return

    this.els.message.element.value = val

    this.els.message.element.dispatchEvent(new Event('input'))
  }

  get productId () {
    return this.dataset.productId ? Number(this.dataset.productId) : null
  }

  set productId (val) {
    this.dataset.productId = val
  }

  get productType () {
    return this.getAttribute('product-type') ?? 'default'
  }

  get variantId () {
    return this.dataset.variantId ? Number(this.dataset.variantId) : null
  }

  set variantId (val) {
    this.dataset.variantId = val
  }
}

if (!customElements.get('gift-drawer')) {
  customElements.define('gift-drawer', GiftDrawer)
}
